/**
 * Shared recurrence math (frontend).
 *
 * SOURCE OF TRUTH: `addFrequency` / `daysInMonth` in
 *   functions/src/functions/http/repeating-invoice-http.ts
 * and `normalizeFrequency` / `FREQUENCIES` in
 *   functions/src/services/accounting/money.ts
 *
 * This is a deliberate, faithful port. The backend scheduler decides the dates
 * a repeating template is ACTUALLY billed on; the calendar only previews them.
 * If the two implementations drift, the owner is shown occurrences that never
 * happen (or misses ones that do), so THESE MUST BE KEPT IN STEP — change one,
 * change the other.
 *
 * WHY UTC-noon: every calculation is anchored at 12:00 UTC and sliced back to
 * 'YYYY-MM-DD'. Noon is far enough from both midnights that no timezone or DST
 * shift can push the instant onto a neighbouring calendar day. Adelaide runs a
 * HALF-HOUR offset (+9:30 / +10:30), which is exactly the kind of thing naive
 * local-Date arithmetic gets wrong. Do not "simplify" this to local Dates.
 */

export const FREQUENCIES = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'] as const;
export type Frequency = typeof FREQUENCIES[number];

/** Number of days in a month. monthIdx0 is 0-based (0 = January). */
export function daysInMonth(year: number, monthIdx0: number): number {
  return new Date(Date.UTC(year, monthIdx0 + 1, 0)).getUTCDate();
}

/**
 * Coerce a frequency string to a known Frequency, or throw.
 *
 * Mirrors the backend, which throws so the caller can PAUSE a template rather
 * than silently re-billing the same date forever. Callers on the frontend
 * should catch and stop generating occurrences rather than guessing.
 */
export function normalizeFrequency(freq: unknown): Frequency {
  const f = String(freq || '').trim().toUpperCase();
  if ((FREQUENCIES as readonly string[]).includes(f)) return f as Frequency;
  throw new Error(`frequency must be one of ${FREQUENCIES.join(', ')}`);
}

/**
 * Advance a recurring date by one period.
 *
 * For monthly/quarterly/yearly the billing DAY is anchored to the template's
 * startDate day-of-month and clamped to the target month's length — so a 31st
 * template goes Jan-31 → Feb-28 → Mar-31. It never rolls over into the next
 * month (which is what `Date.setMonth()` does: Jan-31 + 1 month = Mar-3, and
 * February is skipped entirely) and it never permanently drifts the day down.
 *
 * @param dateStr      current occurrence, 'YYYY-MM-DD'
 * @param frequency    WEEKLY | MONTHLY | QUARTERLY | YEARLY (case-insensitive)
 * @param startDateStr the template's original start date, 'YYYY-MM-DD' — this
 *                     supplies the anchor day. Falls back to dateStr when empty.
 * @returns the next occurrence as 'YYYY-MM-DD'
 * @throws Error on an unrecognised frequency
 */
export function addFrequency(dateStr: string, frequency: string, startDateStr: string): string {
  const freq: Frequency = normalizeFrequency(frequency);
  const cur = new Date(dateStr + 'T12:00:00Z');
  if (freq === 'WEEKLY') {
    cur.setUTCDate(cur.getUTCDate() + 7);
    return cur.toISOString().slice(0, 10);
  }
  const stepMonths = freq === 'MONTHLY' ? 1 : freq === 'QUARTERLY' ? 3 : 12;
  const anchorDay = new Date((startDateStr || dateStr) + 'T12:00:00Z').getUTCDate();
  const targetMonthAbs = cur.getUTCFullYear() * 12 + cur.getUTCMonth() + stepMonths;
  const ty = Math.floor(targetMonthAbs / 12);
  const tm = targetMonthAbs % 12;
  const day = Math.min(anchorDay, daysInMonth(ty, tm));
  return new Date(Date.UTC(ty, tm, day, 12)).toISOString().slice(0, 10);
}
